/**
 * Media Library API (Hybrid: Global + Per-Project)
 *
 * Frontend helpers for the centralized media management system.
 * Media items are stored in Supabase Storage with metadata in KV.
 *
 * - Global media: no templateId (shared across all projects)
 * - Project media: templateId set (scoped to a specific template)
 */

import { apiFetch } from "./apiClient";

export interface MediaItem {
  id: string;
  originalName: string;
  storagePath: string;
  signedUrl: string;
  signedUrlExpiry: number;
  contentType: string;
  size: number;
  folder: string;
  tags: string[];
  alt: string;
  description: string;
  uploadedBy: string;
  templateId?: string;
  copiedFrom?: string;
  scope?: "global" | "project";
  createdAt: number;
  updatedAt: number;
}

export interface MediaListResponse {
  items: MediaItem[];
  total: number;
  page: number;
  pageSize: number;
  folders: string[];
  tags: string[];
}

export interface MediaUploadParams {
  base64: string;
  originalName?: string;
  folder?: string;
  tags?: string[];
  alt?: string;
  description?: string;
  templateId?: string;
}

export async function listMedia(params?: {
  folder?: string;
  tag?: string;
  search?: string;
  page?: number;
  pageSize?: number;
  templateId?: string;
  includeGlobal?: boolean;
}): Promise<MediaListResponse> {
  const qs = new URLSearchParams();
  if (params?.folder) qs.set("folder", params.folder);
  if (params?.tag) qs.set("tag", params.tag);
  if (params?.search) qs.set("search", params.search);
  if (params?.page !== undefined) qs.set("page", String(params.page));
  if (params?.pageSize) qs.set("pageSize", String(params.pageSize));
  if (params?.templateId) qs.set("templateId", params.templateId);
  if (params?.includeGlobal) qs.set("includeGlobal", "1");
  const query = qs.toString();
  return apiFetch(`/media${query ? "?" + query : ""}`);
}

export async function getMediaItem(id: string, templateId?: string): Promise<MediaItem> {
  const qs = templateId ? `?templateId=${encodeURIComponent(templateId)}` : "";
  const data = await apiFetch(`/media/${encodeURIComponent(id)}${qs}`);
  return data.item;
}

export async function uploadMedia(params: MediaUploadParams): Promise<MediaItem> {
  const data = await apiFetch("/media", {
    method: "POST",
    body: JSON.stringify(params),
  });
  return data.item;
}

export async function updateMedia(
  id: string,
  updates: Partial<Pick<MediaItem, "alt" | "description" | "folder" | "tags">>,
  templateId?: string,
): Promise<MediaItem> {
  const qs = templateId ? `?templateId=${encodeURIComponent(templateId)}` : "";
  const data = await apiFetch(`/media/${encodeURIComponent(id)}${qs}`, {
    method: "PUT",
    body: JSON.stringify(updates),
  });
  return data.item;
}

export async function deleteMedia(id: string, templateId?: string): Promise<void> {
  const qs = templateId ? `?templateId=${encodeURIComponent(templateId)}` : "";
  await apiFetch(`/media/${encodeURIComponent(id)}${qs}`, { method: "DELETE" });
}

export async function batchDeleteMedia(ids: string[], templateId?: string): Promise<void> {
  await apiFetch("/media/batch-delete", {
    method: "POST",
    body: JSON.stringify({ ids, templateId: templateId || "" }),
  });
}

export async function refreshMediaUrl(id: string, templateId?: string): Promise<MediaItem> {
  const qs = templateId ? `?templateId=${encodeURIComponent(templateId)}` : "";
  const data = await apiFetch(`/media/${encodeURIComponent(id)}/refresh-url${qs}`, {
    method: "POST",
  });
  return data.item;
}

export async function copyMediaToProject(mediaId: string, targetTemplateId: string): Promise<MediaItem> {
  const data = await apiFetch("/media/copy-to-project", {
    method: "POST",
    body: JSON.stringify({ mediaId, targetTemplateId }),
  });
  return data.item;
}

export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
}
